<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { Radio, Text, TaskPanel, Divider, Button } from "@getprovi/craft-svelte";
  import CartWidgetNotice from "@retailer/svelte/CartWidget/v2/CartWidgetNotice.svelte";

  // stores
  import CurrentRetailerStore from "@retailer/svelte/stores/CurrentRetailerStore";
  import RetailerFulfillmentMethodsStore from "@retailer/svelte/stores/RetailerFulfillmentMethodsStore";
  import { CraftAlertsStore } from "@shared/svelte/Components/Craft/CraftAlertsWrapper.svelte";
  import WarehouseStore from "@shared/svelte/Stores/WarehouseStore";
  import { ProductLineListingStore } from "@retailer/svelte/ProductLines/ProductLineContainer/stores/ProductLineListingStore";

  // services
  import { updateFulfillmentMethod } from "@retailer/services/fulfillment_methods_service";
  import {
    Notice,
    StockLevels,
  } from "@retailer/svelte/CartWidget/v2/Services/CartItemRequestService";
  import { PickupOrDeliveryEntity } from "@shared/services/event_tracking/entities";
  import { OpenTaskPanelFromOOSEntity } from "@shared/services/event_tracking/entities/open_task_panel_from_oos_entity";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { getDataForInventories } from "@retailer/services/pricing_data_service";

  // types
  import type { Warehouse } from "@shared/types/Warehouse";
  import type { Product, InventoryPrice } from "@shared/types/ProductLine";

  export let distributor = null;
  export let open: boolean;
  export let useCustomSave: boolean = false;
  export let selectedStockSiteId: string = "";
  export let skus = [];
  export let products: Product[] = [];
  export let forProductLine: boolean = true;

  const { trackClick } = EventTracking;

  const distributorId = distributor?.id || distributor?.distributor_id;
  const defaultCode = "";

  const dispatch = createEventDispatcher();

  const generateTrackingEntity = warehouseLocationCode => {
    const entityParams = {
      distributorId: distributorId,
      inventoryId: null,
      warehouseId: null,
      warehouseLocationCode: warehouseLocationCode,
      selection: warehouseLocationCode ? "pickup" : "delivery",
    };

    const entity = new PickupOrDeliveryEntity(entityParams);
    return entity;
  };

  let warehouses: Warehouse[] = [];
  let defaultWarehouse: Warehouse = null;
  let hasNextPage: boolean = false;
  let loaded: boolean = false;
  const isPickupOnly: boolean = distributor?.fulfillment_model === "pickup_only";
  const taskPanelTitle = isPickupOnly ? "Select a Pickup Location" : "Select Delivery or Pickup";

  const onOpen = async () => {
    loaded = false;
    const warehouseData = await WarehouseStore.getWarehouses(
      distributorId,
      skus,
      null,
      selectedStockSiteId || distributor?.default_stock_site_id,
      forProductLine,
    );
    warehouses = warehouseData?.data?.warehouses || [];
    defaultWarehouse = warehouseData?.data?.defaultWarehouse;
    loaded = warehouseData?.loaded;
    hasNextPage = warehouseData?.data?.pageInfo?.hasNextPage;
    if (loaded) trackTaskPanelOpen();
  };

  const closeDrawer = (e: any = null): void => {
    if (e?.target?.innerText === "Cancel") {
      trackClick("pickup_or_delivery_task_panel_cancel", generateTrackingEntity(selectedId));
    }
    open = false;
  };

  const updateInventoryPriceData = (inventoryId, unitPrice, casePrice) => {
    ProductLineListingStore.update(n => {
      const { inventoryPrices } = n;
      if (inventoryPrices) {
        const inventoryData = inventoryPrices.find(p => p.inventory_id === inventoryId);
        if (inventoryData) {
          inventoryData.unit_price = unitPrice;
          inventoryData.case_price = casePrice;
        }
      }
      return n;
    });
  };

  const updatePrices = async products => {
    if (distributor?.uses_warehouse_pickup) {
      const inventoryIds = products.map(product => product.inventory_id);
      const pricingData = await getDataForInventories(inventoryIds);
      if (pricingData && pricingData.length) {
        pricingData.forEach(pricing => {
          let price: InventoryPrice = pricing?.inventory_price;
          if (!price) return;
          updateInventoryPriceData(price.inventory_id, price.unit_price, price.case_price);
          const product = products.find(product => product.inventory_id === price.inventory_id);
          product.unit_price = price.unit_price;
          product.case_price = price.case_price;
        });
      }
    }
  };

  const saveSelection = async (): Promise<void> => {
    const newWarehouse =
      selectedId === ""
        ? { name: null, location_code: "" }
        : displayWarehouses.find(warehouse => selectedId === warehouse.location_code);
    if (useCustomSave) {
      dispatch("saveSelection", newWarehouse);
    } else {
      await updateFulfillmentMethod(distributorId, selectedId);
      await RetailerFulfillmentMethodsStore.updateDistributorFulfillmentMethod(distributorId);

      CraftAlertsStore.add({
        message:
          newWarehouse.location_code === ""
            ? `You are now ordering for delivery for ${$CurrentRetailerStore.name}`
            : `You are now ordering for pickup at ${newWarehouse.name} for ${$CurrentRetailerStore.name}`,
        variant: "success",
      });
    }
    if (products.length) updatePrices(products);
    trackClick(
      "pickup_or_delivery_task_panel_save",
      generateTrackingEntity(newWarehouse.location_code),
    );
    closeDrawer();
  };

  const handleSelect = (location_code: string) => {
    selectedId = location_code;
    if (selectedId) {
      trackClick("pickup_or_delivery_select_pickup", generateTrackingEntity(selectedId));
    } else {
      trackClick("pickup_or_delivery_select_delivery", generateTrackingEntity(null));
    }
    dispatch("handleTaskPanelChange");
  };

  const loadMoreWarehouses = async () => {
    const newWarehouseData = await WarehouseStore.getWarehouses(
      distributorId,
      skus,
      $WarehouseStore.data?.pageInfo?.endCursor,
      selectedStockSiteId || distributor?.default_stock_site_id,
      forProductLine,
    );
    const newWarehouses = newWarehouseData?.data?.warehouses;
    displayWarehouses = [...displayWarehouses, ...newWarehouses];
    hasNextPage = newWarehouseData?.data?.pageInfo?.hasNextPage;
  };

  const prioritizeSelectedWarehouse = () => {
    let updatedWarehouses = displayWarehouses;
    const index = updatedWarehouses.findIndex(warehouse => warehouse?.location_code === selectedId);
    if (index !== -1) {
      const [matchedWarehouse] = updatedWarehouses.splice(index, 1);
      updatedWarehouses.unshift(matchedWarehouse);
    }
    displayWarehouses = updatedWarehouses;
  };

  const buildNotice = (stockStatus: string | null): Notice => {
    if (!stockStatus) return { level: "info", message: "Not sold at this location" };
    if (stockStatus.includes("in stock")) return { level: "success", message: stockStatus };

    switch (stockStatus) {
      case StockLevels.inStock:
        return { level: "success", message: stockStatus };
      case StockLevels.lowStock:
        return { level: "warning", message: stockStatus };
      case StockLevels.outOfStock:
        return { level: "error", message: stockStatus };
      case StockLevels.notSoldAtThisLocation:
        return { level: "info", message: stockStatus };
    }
  };

  const iconOverride = (stockStatus: string | null): string | null => {
    if (!stockStatus || stockStatus === StockLevels.notSoldAtThisLocation) return "Info";
    return null;
  };

  const trackTaskPanelOpen = () => {
    let entity = null;
    const selectedWarehouse =
      selectedId === defaultCode
        ? $WarehouseStore.data.defaultWarehouse
        : warehouses.find(warehouse => warehouse?.location_code === selectedId);

    if (
      selectedWarehouse?.stock_status === "Not sold at this location" ||
      selectedWarehouse?.stock_status === "Out of stock"
    ) {
      const entityParams = {
        distributorId: distributorId,
        inventoryId: null,
        warehouseId: null,
        warehouseLocationCode: selectedId,
        selection: selectedId ? "pickup" : "delivery",
      };
      entity = new OpenTaskPanelFromOOSEntity(entityParams);
    } else {
      entity = generateTrackingEntity(selectedId);
    }

    trackClick("pickup_or_delivery_open_task_panel", entity);
  };

  $: showStockStatus = skus.length > 0;
  $: selectedId = selectedStockSiteId || defaultCode;
  $: displayWarehouses = warehouses;
  $: warehouses && prioritizeSelectedWarehouse();
  $: displayViewMoreBtn = hasNextPage;
  $: open && onOpen();
</script>

{#if loaded}
  <TaskPanel
    bind:open
    testId="fulfillment-method-task-panel"
    id="fulfillment-method-task-panel"
    primaryClick={saveSelection}
    secondaryClick={closeDrawer}
    secondaryLabel="Cancel"
    title={taskPanelTitle}
  >
    <div class="flex flex-col gap-32">
      {#if !isPickupOnly}
        <div class="flex flex-col gap-8">
          <div>
            <Text size="default" semibold>Delivery Options</Text>
            <Divider />
          </div>
          <Radio
            bind:group={selectedId}
            on:change|once={() => handleSelect(defaultCode)}
            checked={selectedId === defaultCode}
            id=""
            label="Deliver to {$CurrentRetailerStore.name}"
            value=""
            testId="delivery-radio"
          >
            {#if showStockStatus}
              <CartWidgetNotice
                notice={buildNotice(defaultWarehouse?.stock_status)}
                iconOverride={iconOverride(defaultWarehouse?.stock_status)}
              />
            {/if}
          </Radio>
        </div>
      {/if}
      <div class="flex flex-col gap-8">
        {#if !isPickupOnly}
          <Text size="default" semibold>Pickup Options</Text>
          <Divider />
        {/if}
        <div class="flex flex-col gap-16">
          {#each displayWarehouses as warehouse (warehouse.location_code)}
            <div class="fizz-flex-row fizz-space-between">
              <Radio
                bind:group={selectedId}
                on:change|once={() => handleSelect(warehouse.location_code)}
                checked={selectedId === warehouse.location_code}
                id={warehouse.location_code}
                label={warehouse.formatted_name}
                value={warehouse.location_code}
                testId="pickup-radio-{warehouse.location_code}"
              >
                <div class:fizz-stack-8={showStockStatus}>
                  <Text size="sm" color="neutral">{warehouse.formatted_address}</Text>
                </div>
                {#if showStockStatus}
                  <CartWidgetNotice
                    notice={buildNotice(warehouse.stock_status)}
                    iconOverride={iconOverride(warehouse.stock_status)}
                  />
                {/if}
              </Radio>
              {#if warehouse.distance_from_retailer}
                <div
                  style="width: 15%;"
                  class="fizz-flex-row fizz-align-self-start fizz-justify-end"
                >
                  <Text size="sm" color="neutral">{warehouse.distance_from_retailer}</Text>
                </div>
              {/if}
            </div>
          {/each}
        </div>
      </div>
      {#if displayViewMoreBtn}
        <div class="fizz-flex-row fizz-align-self-center">
          <Button
            variant="tertiary"
            on:click={() => {
              loadMoreWarehouses();
            }}>View More Options</Button
          >
        </div>
      {/if}
    </div>
  </TaskPanel>
{/if}
